<template>
  <AppNavbar />
  <div class="vx-admin">
    <div style="max-width: 1100px">

      <div class="mb-3">
        <div class="vx-migas mb-1">
          <span>Operación</span><span class="sep">/</span><span>Pendientes de facturar</span>
        </div>
        <h1 class="vx-titulo">Pendientes de facturar</h1>
        <div class="vx-ayuda mt-1">
          Ventas cobradas con recibo de dinero, sin factura electrónica emitida.
          La factura que las agrupa se emite fuera de este sistema; acá se
          listan para poder armarla y se marcan para no facturarlas dos veces.
        </div>
      </div>

      <!-- Las cifras primero: la pregunta es de plata, no de filas -->
      <div class="row g-3 mb-3">
        <div class="col-md-4">
          <div class="vx-panel p-3">
            <div class="vx-ayuda">Sin facturar</div>
            <div class="vx-num" style="font-size: 26px; font-weight: 680"
                 data-test="total-diferido">
              {{ formatearMonto(resumen.monto_total || 0) }}
            </div>
          </div>
        </div>
        <div class="col-md-4">
          <div class="vx-panel p-3">
            <div class="vx-ayuda">Ventas</div>
            <div class="vx-num" style="font-size: 26px; font-weight: 680">
              {{ resumen.cantidad || 0 }}
            </div>
          </div>
        </div>
        <div class="col-md-4">
          <div class="vx-panel p-3">
            <div class="vx-ayuda">La más antigua</div>
            <div class="vx-num" style="font-size: 26px; font-weight: 680"
                 data-test="mas-antigua">
              {{ resumen.mas_antigua ? fechaCorta(resumen.mas_antigua) : '—' }}
            </div>
            <!-- No es decorativo: una diferida de hace tres meses no es lo
                 mismo que una de ayer, aunque el monto sea igual. -->
            <div v-if="diasDeAtraso > 30" class="vx-error mt-1" data-test="aviso-antiguedad">
              Hace {{ diasDeAtraso }} días sin facturar.
            </div>
          </div>
        </div>
      </div>

      <div class="vx-panel mb-3">
        <div class="vx-panel-cabecera">
          <h2 class="vx-panel-titulo">Qué falta facturar</h2>
          <div class="d-flex gap-2 align-items-center">
            <input v-model="filtros.desde" type="date" class="vx-campo vx-campo-sm"
                   aria-label="Desde">
            <input v-model="filtros.hasta" type="date" class="vx-campo vx-campo-sm"
                   aria-label="Hasta">
            <button class="vx-btn vx-btn-sm" @click="cargar">Aplicar</button>
          </div>
        </div>

        <div v-if="cargando" class="vx-vacio">
          <div class="spinner-border spinner-border-sm text-secondary" role="status"></div>
          <span class="vx-vacio-texto">Cargando…</span>
        </div>

        <div v-else-if="!ventas.length" class="vx-vacio" data-test="sin-diferidas">
          <i class="bi bi-check2-circle" style="font-size: 22px; color: var(--vx-texto-4)"></i>
          <span class="vx-vacio-titulo">No hay nada pendiente de facturar</span>
          <span class="vx-vacio-texto">
            Todo lo cobrado con recibo ya quedó cubierto por una factura.
          </span>
        </div>

        <table v-else class="vx-tabla" data-test="tabla-diferidas">
          <thead>
            <tr>
              <th style="width: 36px">
                <input type="checkbox" :checked="todasElegidas" aria-label="Elegir todas"
                       @change="alternarTodas">
              </th>
              <th>Fecha</th>
              <th>Pagador</th>
              <th>Qué se vendió</th>
              <th class="vx-col-num">Importe</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="v in ventas" :key="v.id_venta">
              <td>
                <input type="checkbox" :checked="elegidas.has(v.id_venta)"
                       :aria-label="`Elegir venta ${v.id_venta}`"
                       @change="alternar(v.id_venta)">
              </td>
              <td class="vx-num">{{ fechaCorta(v.fecha_emision) }}</td>
              <td>
                <div class="vx-celda-principal">{{ v.nombre_razon_social || 'Consumidor Final' }}</div>
                <div class="vx-celda-sub vx-num">{{ v.nro_documento || 'Sin documento' }}</div>
              </td>
              <td>
                <div v-for="(d, i) in (v.detalles || [])" :key="i" class="vx-celda-sub">
                  {{ d.cantidad }} × {{ d.descripcion }}
                </div>
              </td>
              <td class="vx-col-num vx-num">{{ formatearMonto(v.total_iva_incluido) }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-if="error" class="alert alert-danger py-2 px-3 mb-3" style="font-size: 13.5px"
           role="alert" data-test="error-consolidar">
        {{ error }}
      </div>

      <div v-if="ventas.length && puedeConsolidar" class="vx-panel">
        <div class="vx-panel-cabecera">
          <h2 class="vx-panel-titulo">Dar por facturadas</h2>
        </div>
        <div class="p-3">
          <div class="vx-nota mb-3">
            <i class="bi bi-info-circle"></i>
            <span>
              Esto <strong>no emite ninguna factura</strong>: sólo deja constancia de
              que las ventas elegidas quedaron cubiertas por una factura que ya
              emitiste. Se guarda quién lo marcó y cuándo.
            </span>
          </div>

          <div class="row g-3 align-items-end">
            <div class="col-md-5">
              <label class="vx-etiqueta" for="referencia">Número de la factura</label>
              <input id="referencia" v-model.trim="referencia" class="vx-campo vx-num"
                     placeholder="001-001-0000123">
            </div>
            <div class="col-md-4">
              <div class="vx-ayuda">Elegidas</div>
              <div class="vx-num" style="font-size: 18px; font-weight: 620"
                   data-test="total-elegido">
                {{ elegidas.size }} · {{ formatearMonto(totalElegido) }}
              </div>
            </div>
            <div class="col-md-3 text-end">
              <button class="vx-btn vx-btn-primario" data-test="btn-consolidar"
                      :disabled="!elegidas.size || !referencia || guardando"
                      @click="consolidar">
                <span v-if="guardando" class="spinner-border spinner-border-sm"></span>
                {{ guardando ? 'Guardando…' : 'Dar por facturadas' }}
              </button>
            </div>
          </div>
        </div>
      </div>

    </div>
  </div>
</template>

<script setup>
// Ventas diferidas — CR-E9-002 · D33, D34.
//
// **Este sistema no emite la factura agrupada.** El cliente la hace por fuera.
// Lo que sin esta pantalla no se podría es saber QUÉ falta facturar: sin ella,
// las ventas cobradas con recibo quedan registradas pero invisibles, y el
// riesgo real de D33 no es cobrar sin documento — es olvidarse de que pasó.

import { ref, reactive, computed, onMounted } from 'vue'
import AppNavbar from '@/components/AppNavbar.vue'
import { useNotificationStore } from '@/stores/notifications'
import {
  listarVentasDiferidas, consolidarVentas, formatearMonto, fechaCorta
} from '@/services/cobranzaService'
import { hasPermission } from '@/services/authSessionService'

const notificaciones = useNotificationStore()

const ventas = ref([])
const resumen = ref({})
const elegidas = ref(new Set())
const referencia = ref('')
const cargando = ref(false)
const guardando = ref(false)
const error = ref('')
const filtros = reactive({ desde: '', hasta: '' })

const puedeConsolidar = computed(() => hasPermission('consolidar_ventas'))

const todasElegidas = computed(
  () => ventas.value.length > 0 && elegidas.value.size === ventas.value.length
)

const totalElegido = computed(() => ventas.value
  .filter((v) => elegidas.value.has(v.id_venta))
  .reduce((acc, v) => acc + Number(v.total_iva_incluido || 0), 0))

// Cuánto hace que hay plata cobrada sin documento. Es el número que dice si
// esto se está yendo de las manos.
const diasDeAtraso = computed(() => {
  if (!resumen.value.mas_antigua) return 0
  const dias = (Date.now() - new Date(resumen.value.mas_antigua).getTime()) / 86400000
  return Math.floor(dias)
})

function alternar (id) {
  const copia = new Set(elegidas.value)
  if (copia.has(id)) copia.delete(id)
  else copia.add(id)
  elegidas.value = copia
}

function alternarTodas () {
  elegidas.value = todasElegidas.value
    ? new Set()
    : new Set(ventas.value.map((v) => v.id_venta))
}

async function cargar () {
  cargando.value = true
  error.value = ''
  try {
    const r = await listarVentasDiferidas({ desde: filtros.desde, hasta: filtros.hasta })
    ventas.value = r?.ventas || []
    resumen.value = r?.resumen || {}
    elegidas.value = new Set()
  } catch (err) {
    error.value = err?.userMessage || 'No se pudieron leer las ventas pendientes.'
    ventas.value = []
  } finally {
    cargando.value = false
  }
}

async function consolidar () {
  error.value = ''
  guardando.value = true
  try {
    const r = await consolidarVentas({
      venta_ids: [...elegidas.value],
      referencia: referencia.value
    })
    // Se informa la diferencia en vez de callarla: si eligió diez y se
    // marcaron ocho, dos ya estaban facturadas y tiene derecho a saberlo
    // antes de creer que su factura cubre las diez.
    notificaciones.notify({
      type: r.omitidas ? 'warning' : 'success',
      message: r.omitidas
        ? `${r.marcadas} marcadas. ${r.omitidas} ya estaban facturadas y no se tocaron.`
        : `${r.marcadas} ventas dadas por facturadas con ${r.referencia}.`
    })
    referencia.value = ''
    await cargar()
  } catch (err) {
    error.value = err?.userMessage || 'No se pudieron marcar las ventas.'
  } finally {
    guardando.value = false
  }
}

onMounted(cargar)
</script>
